import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Pencil, Trash2, Flag, EyeOff, MessageSquare, Clock } from "lucide-react";
import { toast } from "sonner";

import { normalizePostDetail, contentPermissions, audienceLabel, textLimit, savedPost, savedComment, removed, writeOutcome, postHref, UNCLEAR_TEXT, UNCLEAR_REMOVE_TEXT, POST_MAX, COMMENT_MAX } from "@/lib/community";
import { excerpt } from "@/lib/v3Views";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { mutate } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { ReportDialog } from "../components/ReportDialog";
import { PageHeader, Loading, Unavailable, PrivacyNote, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Field, V3Actions, AsideCard, DetailRow, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { V3TextArea, AreaCard, ResponsibleCard, V3_SMALL, V3_GHOST, V3_LINK } from "../components/v3app";
import { ProtectedImage } from "../components/ProtectedImage";
import { PostMeta, postFooter } from "./Community";
import { EditBox, RemovePanel, CommentItem, UnclearNote, useGuard } from "./PostParts";

const back = { to: "/community", label: "Zur Community" };
const VISIBLE_FOR = { group: "Dieser Beitrag ist nur für aufgenommene Mitglieder des Kreises sichtbar.", community: "Dieser Beitrag ist für alle angemeldeten Mitglieder der Mitglieder-Community sichtbar." };
const ANSWER_IN = { group: "Deine Antwort erscheint bei diesem Beitrag im gewählten Kreis.", community: "Deine Antwort erscheint bei diesem Beitrag in der Mitglieder-Community." };
const RECIPIENT = { group: "Geschlossener Kreis", community: "Alle angemeldeten Mitglieder" };

// M44 – check first, then send; after an unclear POST the form stays locked (text kept in memory only) until the member unlocks it.
function CommentForm({ post, onAdded, onCancel }) {
  const postId = post.id;
  const [value, setValue] = useState("");
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState("");
  const [unclear, setUnclear] = useState(false);
  const [busy, setBusy] = useState(false);
  const guard = useGuard();
  const limit = textLimit(value, COMMENT_MAX);
  const check = () => { setError(limit.error); setChecked(!limit.error); };
  const send = async () => {
    if (unclear) return;
    if (limit.error) { setError(limit.error); return; }
    setBusy(true); setError("");
    const r = await guard(() => mutate("post", `/member/posts/${encodeURIComponent(postId)}/comments`, { body: limit.value }, "Der Kommentar wurde nicht gesendet"));
    if (r === undefined) return;
    setBusy(false);
    const c = savedComment(r.data);
    const o = writeOutcome(r, c);
    if (o.state === "unclear") { setUnclear(true); return; }
    if (o.state === "rejected") { setError(o.error); return; }
    setValue(""); onAdded(c); toast.success("Kommentar gesendet.");
  };
  const status = unclear ? "Unklar · Bitte Stand prüfen" : busy ? "Wird gesendet …" : checked ? "Geprüft · Noch nicht gesendet" : "Entwurf · Noch nicht gesendet";
  return (<>
    <V3Title title="Kommentar verfassen" intro={ANSWER_IN[post.audience.type] || "Deine Antwort erscheint bei diesem Beitrag."} testid="post-detail-title" focus />
    <V3Columns layout="detail" aside={<><AsideCard icon={MessageSquare} title="Nachrichtenvorschau" text={excerpt(value, 160) || "Noch kein Text"} testid="comment-preview" />
      <AsideCard icon={Clock} tone="white" title="Versandstatus" text={status} testid="comment-send-status" /></>} main={
      <div data-testid="comment-form" data-m="M44">
        <V3Field id="comment-post" label="Beitrag" value={excerpt(post.body, 90) || "Beitrag"} readOnly />
        <V3Field id="comment-recipient" label="Empfänger" value={`${audienceLabel(post.audience)} · ${RECIPIENT[post.audience.type] || "Zielgruppe unbekannt"}`} readOnly />
        <V3TextArea id="comment-new" label="Dein Text" max={COMMENT_MAX} value={value} onChange={(e) => { setValue(e.target.value); setChecked(false); }} error={value && limit.length > COMMENT_MAX ? limit.error : ""} />
        <DetailRow label="Status" value={status} testid="comment-status" />
        {error && <div className="mt-[12px]"><ErrorNote testid="comment-send-error">{error}</ErrorNote></div>}
        {unclear && <div className="mt-[12px]"><UnclearNote testid="comment-unclear" text={UNCLEAR_TEXT} checkTo={postHref(postId)} checkLabel="Beitrag prüfen" onUnlock={() => setUnclear(false)} /></div>}
        <V3Actions>
          {checked ? <button type="button" onClick={send} disabled={busy || unclear} data-testid="comment-send" className={V3_PRIMARY}>{busy ? "Wird gesendet …" : "Antwort senden"}</button>
            : <button type="button" onClick={check} disabled={unclear} data-testid="comment-check" className={V3_PRIMARY}>Antwort prüfen</button>}
          <button type="button" onClick={onCancel} disabled={busy} data-testid="comment-cancel" className={V3_SECONDARY}>Abbrechen</button>
        </V3Actions>
      </div>
    } />
  </>);
}

function PostBody({ post, perm, onEdit, onRemove, onReport }) {
  const hidden = post.status === "hidden";
  return (
    <div className="space-y-[16px]" data-testid="post-detail-post">
      <PostMeta post={post} />
      {hidden ? <p className="flex gap-2 rounded-[12px] bg-v3-sand px-[16px] py-[12px] text-[14px] text-v3-amber" data-testid="post-detail-hidden"><EyeOff className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {post.isMine ? `Dein Beitrag wurde von der Moderation ausgeblendet und ist für andere nicht sichtbar.${post.moderationNote ? ` Hinweis: ${post.moderationNote}` : ""}` : "Dieser Beitrag ist ausgeblendet."}</p>
        : <p className="whitespace-pre-wrap break-words px-[2px] text-[19px] font-bold leading-[26px] text-v3-ink lg:text-[22px] lg:leading-[31px]" data-testid="post-detail-body">{post.body}</p>}
      {!hidden && post.image && <ProtectedImage raw={post.image} testid="post-detail-image" />}
      {(perm.edit || perm.remove || perm.report) && (
        <div className="flex flex-wrap gap-2">
          {perm.edit && <button type="button" onClick={onEdit} data-testid="post-detail-edit" className={V3_SMALL}><Pencil className="h-4 w-4" aria-hidden="true" /> Bearbeiten</button>}
          {perm.remove && <button type="button" onClick={onRemove} data-testid="post-detail-remove" className={V3_SMALL}><Trash2 className="h-4 w-4" aria-hidden="true" /> Entfernen</button>}
          {perm.report && <button type="button" onClick={onReport} data-testid="post-detail-report" className={V3_GHOST}><Flag className="h-4 w-4" aria-hidden="true" /> Melden</button>}
        </div>
      )}
    </div>
  );
}

const aside = (post, title = "Dein Bereich") => <><AreaCard title={title} text={excerpt(post.body, 70) || audienceLabel(post.audience)} testid="post-detail-area" /><ResponsibleCard /></>;

// M43 / M44 / M45 / M46: post detail with comments; only own content can be changed. Modes stay in memory only.
function PostDetail({ id }) {
  const navigate = useNavigate();
  const res = useCore(`/member/posts/${encodeURIComponent(id)}`, (d) => ({ detail: normalizePostDetail(d) }));
  const [local, setLocal] = useState(null);
  const [mode, setModeRaw] = useState("view");
  const [moved, setMoved] = useState(false);
  const setMode = (m) => { setMoved(true); setModeRaw(m); };
  const [report, setReport] = useState(null);
  if (res.error === "forbidden") return <><PageHeader title="Kein Zugriff" back={back} /><Unavailable testid="post-detail-forbidden">Mit dem angemeldeten Konto hast du keinen Zugriff: Du gehörst nicht zur Zielgruppe dieses Beitrags. Es werden keine Inhalte angezeigt.</Unavailable></>;
  if (res.error === "notfound") return <><PageHeader title="Beitrag nicht verfügbar" back={back} /><Unavailable testid="post-detail-notfound">Dieser Beitrag wurde entfernt, existiert nicht oder ist für das angemeldete Konto nicht sichtbar.</Unavailable></>;
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (!res.data) return <Loading label="Beitrag wird geladen …" />;
  const detail = local || res.data.detail;
  if (!detail) return <><PageHeader title="Beitrag" back={back} /><Unavailable testid="post-detail-format">Die Antwort des Servers hat ein unerwartetes Format. Es werden keine Inhalte angezeigt.</Unavailable></>;
  const { post, comments } = detail;
  const perm = contentPermissions(post, { post: true });
  const path = `/member/posts/${encodeURIComponent(post.id)}`;
  const refreshOnGone = (status) => { if (status === 403 || status === 404) { setLocal(null); setMode("view"); res.reload(); } };
  const checkTo = postHref(post.id);
  const savePost = async (body) => {
    const r = await mutate("patch", path, { body }, "Der Beitrag wurde nicht geändert");
    const saved = savedPost(r.data);
    const o = writeOutcome(r, saved && saved.id === post.id ? saved : null);
    if (o.state === "rejected") refreshOnGone(r.status);
    if (o.state !== "saved") return o;
    setLocal({ post: saved, comments }); setMode("view"); toast.success("Änderung gespeichert."); return null;
  };
  const removePost = async () => {
    const r = await mutate("delete", path, undefined, "Der Beitrag wurde nicht entfernt");
    const o = writeOutcome(r, removed(r.data), UNCLEAR_REMOVE_TEXT);
    if (o.state === "rejected") refreshOnGone(r.status);
    if (o.state !== "saved") return o;
    toast.success("Beitrag entfernt."); navigate("/community", { replace: true }); return null;
  };
  const setComments = (next) => setLocal({ post: { ...post, commentsCount: next.length }, comments: next });
  const reach = post.audience.type === "group" ? "Der Beitrag verschwindet aus diesem Kreis." : "Der Beitrag verschwindet aus der Mitglieder-Community.";

  if (mode === "comment" && perm.comment) return (
    <div data-testid="post-detail" data-m="M44"><CommentForm post={post} onCancel={() => setMode("view")} onAdded={(c) => { setComments([...comments, c]); setMode("view"); }} /></div>
  );
  if (mode === "edit" && perm.edit) return (
    <div data-testid="post-detail" data-m="M45">
      <V3Title title="Eigenen Beitrag bearbeiten" intro="Die bestehende Zielgruppe bleibt erhalten – ändern kannst du sie hier nicht." testid="post-detail-title" focus />
      <V3Columns layout="detail" aside={<><AsideCard icon={MessageSquare} title="Nachrichtenvorschau" text={excerpt(post.body, 160)} /><AsideCard icon={Clock} tone="white" title="Versandstatus" text="Änderung erst nach dem Speichern sichtbar" /></>} main={<>
        <V3Field id="post-edit-audience" label="Zielgruppe" value={audienceLabel(post.audience)} readOnly hint="Die Zielgruppe kann nach dem Veröffentlichen nicht geändert werden." />
        <EditBox id="post" label="Dein Text" max={POST_MAX} initial={post.body} onSave={savePost} onCancel={() => setMode("view")} checkTo={checkTo} />
      </>} />
    </div>
  );
  if (mode === "remove" && perm.remove) return (
    <div data-testid="post-detail" data-m="M46">
      <V3Title title="Eigenen Beitrag entfernen" intro="Prüfe die Wirkung, bevor du deinen eigenen Beitrag entfernst." testid="post-detail-title" focus />
      <V3Columns layout="detail" aside={aside(post, "Ausgewählter Vorgang")} main={
        <RemovePanel onRemove={removePost} onKeep={() => setMode("view")} checkTo={checkTo} rows={[
          ["Eigener Beitrag", excerpt(post.body, 90) || "Ausgeblendeter Beitrag", "post"], ["Wirkung", reach, "effect"],
          ["Kommentare", comments.length ? `${comments.length} ${comments.length === 1 ? "Kommentar ist" : "Kommentare sind"} danach ebenfalls nicht mehr sichtbar.` : "Keine Kommentare betroffen.", "comments"],
          ["Rückgängig machen", "Nicht möglich", "undo"]]} />
      } />
    </div>
  );
  return (
    <div data-testid="post-detail" data-m="M43">
      <V3Title title="Beitrag und Kommentare" intro={VISIBLE_FOR[post.audience.type] || audienceLabel(post.audience)} testid="post-detail-title" focus={moved} />
      <V3Columns layout="feed" aside={aside(post)} main={<>
        <PostBody post={post} perm={perm} onEdit={() => setMode("edit")} onRemove={() => setMode("remove")}
          onReport={() => setReport({ memberId: post.author.id, name: post.author.name, postId: post.id })} />
        <p className="mt-[16px] text-[13.5px] text-v3-muted" data-testid="post-detail-footer">{postFooter({ ...post, commentsCount: perm.comment ? comments.length : post.commentsCount })}</p>
        {perm.comment ? (
          <section className="mt-[12px]" aria-label={`Kommentare (${comments.length})`} data-testid="post-comments">
            {comments.length ? <ul className="space-y-[11px]" data-testid="comment-list">{comments.map((c) => (
              <CommentItem key={c.id} c={c} postId={post.id} onChanged={(s) => setComments(comments.map((x) => (x.id === s.id ? s : x)))}
                onRemoved={(cid) => { setComments(comments.filter((x) => x.id !== cid)); toast.success("Kommentar entfernt."); }}
                onReport={(x) => setReport({ memberId: x.author.id, name: x.author.name, postId: post.id, commentId: x.id })} />))}</ul>
              : <p className="text-[14px] text-v3-muted" data-testid="comment-empty">Noch keine Kommentare.</p>}
            <V3Actions className="mt-[17px]"><button type="button" onClick={() => setMode("comment")} data-testid="comment-open" className={V3_PRIMARY}>Antwort verfassen</button></V3Actions>
          </section>
        ) : <p className="mt-[12px] text-[14px] text-v3-muted" data-testid="post-comments-closed">Zu ausgeblendeten Beiträgen gibt es keine Kommentare.</p>}
        <div className="mt-[24px]"><PrivacyNote>Beiträge, Kommentare und Meldungen enthalten nie Angaben aus deinem Access Pass.</PrivacyNote></div>
        <Link to="/community" className={`${V3_LINK} mt-[8px] text-[14px]`} data-testid="post-detail-back">Zurück zur Community</Link>
      </>} />
      <ReportDialog open={!!report} onOpenChange={(o) => !o && setReport(null)} reportedMemberId={report?.memberId} reportedName={report?.name} postId={report?.postId} commentId={report?.commentId}
        title={report?.commentId ? "Kommentar melden" : "Beitrag melden"} description="Die Moderation von VISIBILIS prüft deine Meldung vertraulich. Es werden keine Access-Pass-Angaben übermittelt." />
    </div>
  );
}

// Keyed by account and id, so no previous private detail survives a switch.
export default function PostDetailRoute() {
  const { id } = useParams();
  const { member } = useMemberAuth();
  return <PostDetail key={`${member?.id || "anon"}:${id}`} id={id} />;
}
